import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { UserService } from './user.service';

var getHeaders = {
  'Accept': 'application/json'
};

/**
 * @typedef {import('types/api').V1.SheetFromAPI} SheetsService.SheetFromAPI
 * @typedef {import('types/api').V1.PendingSheetFromAPI} SheetsService.PendingSheetFromAPI
 */

/**
 * @typedef {{ eventName: 'pending_sheets', handler: (sheets: SheetsService.PendingSheetFromAPI[]) => void }} SheetsService.PendingListener
 * @typedef {{ eventName: 'sheets', handler: (sheets: SheetsService.SheetFromAPI[]) => void }} SheetsService.SheetsListener
 * @typedef {SheetsService.PendingListener | SheetsService.SheetsListener} SheetsService.Listener
 */


/**
 * When polling for pending sheets, we may get an empty result back.
 * This might be a good time to stop polling, but we want to get a
 * certain number of empty results back, first. This is that number.
 */
var PENDING_EMPTY_POLLS_PERMITTED = 5;


export class SheetsService {

  /** @type {BehaviorSubject<SheetsService.SheetFromAPI[]>} */
  _sheetsSubject = new BehaviorSubject([]);
  /** @type {BehaviorSubject<SheetsService.PendingSheetFromAPI[]>} */
  _pendingSheetsSubject = new BehaviorSubject([]);

  sheets$ = this._sheetsSubject
    .pipe(distinctUntilChanged());
  pendingSheets$ = this._pendingSheetsSubject
    .pipe(distinctUntilChanged());

  /**
   * @return {ng.IPromise<unknown>}
   */
  fetchSheets() {
    return this._SheetResource.query()
      .$promise
      .then(resp => this._sheetsSubject.next(resp.items));
  };

    /**
   * @param {object} args
   * @param {string} args.template
   * @param {string} args.spreadsheetTitle
   * @param {string[]} args.accounts
   * @return {ng.IPromise<SheetsService.SheetFromAPI | { error: { message: string }}>}
   */
  createSheet(args) {
    return this._SheetResource.save({
        template: args.template,
        spreadsheetTitle: args.spreadsheetTitle,
        accounts: args.accounts,
      }).$promise
      .then(resp => {
        return this.startPendingSheetPoll()
          .then(() => resp)
      });
  }


  /**
   * @param {string} sheetId
   * @return {ng.IPromise<unknown>}
   */
  deleteSheet(sheetId) {
    return this._SheetResource.delete({ id: sheetId })
      .$promise
      .finally(() => {
        return this.fetchSheets();
      });
  }

  /**
   * @param {string} sheetId
   * @param {string[]} accountIds
   * @return {ng.IPromise<unknown>}
   */
  changeLinkedAccounts(sheetId, accountIds) {
    return this._SheetResource.update(
      { id: sheetId },
      { accounts: accountIds.map(function(id) { return { id: id }; }) },
    ).$promise;
  }

  /**
   * @param {object} sheet
   * @param {string} sheet.id
   * @return {ng.IPromise<unknown>}
   */
  retryPendingSheet(sheet) {
    return this.startPendingSheetPoll()
      .then(() => {
        return this._PendingSheetResource.retry(sheet).$promise;
      });
  };

  /**
   * @param {object} sheet
   * @param {string} sheet.id
   * @return {ng.IPromise<unknown>}
   */
  cancelPendingSheet(sheet) {
    return this.startPendingSheetPoll()
      .then(() => {
        return this._PendingSheetResource.delete(sheet).$promise;
      });
  }

  _pendingInterval = null;
  _pendingEmptyPollsPermitted = 0;

  _cancelPendingPoll() {
    if (!!this._pendingInterval) {
      this._$interval.cancel(this._pendingInterval);
      this._pendingInterval = null;
      this._pendingEmptyPollsPermitted = 0;
    }
  };

  /**
   * @return {ng.IPromise<unknown>}
   */
  startPendingSheetPoll() {
    this._pendingEmptyPollsPermitted = PENDING_EMPTY_POLLS_PERMITTED;

    if (!!this._pendingInterval) {
      return this._$q.resolve({status: 'already_polling'});
    }

    var self = this;

    this._pendingInterval = this._$interval(
      () => {
        if (this._pendingEmptyPollsPermitted <= 0) {
          this._cancelPendingPoll();
          return null;
        }

        return this._PendingSheetResource.query().$promise
          .then((pendingSheets) => {
            if (pendingSheets.length == 0) {
              this._pendingEmptyPollsPermitted -= 1;
            }

            this._pendingSheetsSubject.next(pendingSheets);
          })
          .catch((err) => {
            console.error("CAUGHT: ", err);
            this._pendingEmptyPollsPermitted -= 1;
          });
      },
      500
    );

    return this._$q.resolve({ status: 'ok' });
  }

  /** @type {import('./user.service').UserService} */
  _UserService;
  /** @type {import('api-services/sheet-resource.factory').ISheetResource} */
  _SheetResource;
  /** @type {import('api-services/pending-sheet-resource.factory').IPendingSheetResource} */
  _PendingSheetResource;
  /** @type {ng.IIntervalService} */
  _$interval;
  /** @type {ng.IQService} */
  _$q;

  /**
   * @param {SheetsService['_UserService']} UserService
   * @param {SheetsService['_SheetResource']} SheetResource
   * @param {SheetsService['_PendingSheetResource']} PendingSheetResource
   * @param {SheetsService['_$interval']} $interval
   * @param {SheetsService['_$q']} $q
   */
  constructor(
    UserService,
    SheetResource,
    PendingSheetResource,
    $interval,
    $q,
  ) {
    this._UserService = UserService;
    this._SheetResource = SheetResource;
    this._PendingSheetResource = PendingSheetResource;
    this._$interval = $interval;
    this._$q = $q;

    this.pendingSheets$
      .subscribe(_ => this.fetchSheets());

    UserService.user$.pipe(
      distinctUntilChanged((a, b) => angular.equals(a?.sheetIds, b?.sheetIds)),
    ).subscribe(user => {
      if (user != null) {
        return this.fetchSheets();
      }
    });
  }
}

export default [
  'UserService',
  'SheetResource',
  'PendingSheetResource',
  '$interval',
  '$q',
  SheetsService
];



